
import { Component, Vue } from 'vue-property-decorator';
import BillingRepository from '@/api/repositories/BillingRepository';
import { RepositoryFactory } from '@/api/RepositoryFactory';
import Billing from '@/models/Billing';
import moment from 'moment';
import { csvFileName, downloadCsv, toCsv } from '@/misc/csv';
import { downloadTablePdf, reportSubtitle } from '@/misc/pdf';
import { ReportColumn, reportFileName } from '@/misc/report';

const billingRepository: BillingRepository = RepositoryFactory.get('billing');

@Component({
    components: {
        BillingTableComponent: () => import(
            /* webpackChunkName: "BillingTableComponent" */
            '@/components/BillingTable.component.vue')
    }
})
export default class RidesView extends Vue {
    public startMenu: boolean = false;
    public endMenu: boolean = false;
    public startDate: string | null = null;
    public endDate: string | null = null;

    private search: string = '';

    async created() {
        const today = moment(new Date());
        this.endDate = today.format('yyyy-MM-DD');
        today.set({ date: 1 });
        this.startDate = today.format('yyyy-MM-DD');
        await this.generateClicked();
    }

    public billingData: Billing[] | null = null;

    private get billings(): Billing[] {
        return this.billingData ?? [];
    }

    public async generateClicked() {
        if (!this.startDate || !this.endDate) {
            return;
        }

        const response = await billingRepository.get({ from: this.startDate, to: this.endDate });
        this.billingData = Billing.parseFromArray(response.data) as Billing[];
    }

    /**
     * Mirrors the on-screen table minus its Options column, which holds the
     * per-row PDF button rather than data. One row per company, exactly as
     * generated — this screen has always been cross-company.
     */
    private get billingReportColumns(): ReportColumn<Billing>[] {
        return [
            { header: this.$t('BILLING.TABLE.NAME').toString(), value: (b) => b.company?.name },
            { header: this.$t('BILLING.TABLE.PHONE').toString(), value: (b) => this.toPhone(b.company?.phone) },
            { header: this.$t('BILLING.TABLE.RIDES').toString(), value: (b) => b.rides?.length ?? 0, total: true },
            { header: this.$t('BILLING.TABLE.CASH_EARNINGS').toString(), value: (b) => b.billingDetails?.cashEarnings, total: true },
            { header: this.$t('BILLING.TABLE.PAYTODAY_EARNINGS').toString(), value: (b) => b.billingDetails?.paytodayEarnings, total: true },
            { header: this.$t('BILLING.TABLE.PAYTODAY_FEE').toString(), value: (b) => b.billingDetails?.paytodayFee, total: true },
            { header: this.$t('BILLING.TABLE.SHARE').toString(), value: (b) => b.billingDetails?.share, total: true },
            { header: this.$t('BILLING.TABLE.EARNINGS').toString(), value: (b) => b.billingDetails?.earnings, total: true }
        ];
    }

    private exportBillingCsv(): void {
        const rows = this.billings;
        if (!rows.length) {
            this.$notifyInfoSimplified('EXPORT.EMPTY');
            return;
        }

        downloadCsv(
            csvFileName('billing', null, this.startDate, this.endDate),
            toCsv(rows, this.billingReportColumns)
        );
    }

    private async exportBillingPdf(): Promise<void> {
        const rows = this.billings;
        if (!rows.length) {
            this.$notifyInfoSimplified('EXPORT.EMPTY');
            return;
        }

        await downloadTablePdf({
            fileName: reportFileName('billing', null, this.startDate, this.endDate, 'pdf'),
            title: this.$t('EXPORT.TITLE.BILLING').toString(),
            subtitle: reportSubtitle(null, this.startDate, this.endDate),
            rows,
            columns: this.billingReportColumns
        });
    }

    private toPhone(phone: { prefix?: string, phoneNumber?: string } | undefined): string {
        if (!phone?.prefix || !phone?.phoneNumber) return '';
        return `${phone.prefix} ${phone.phoneNumber}`;
    }
}
